/**
 * consent.ts - Cookie consent store.
 *
 * Nothing that sets a non-essential cookie may load until the visitor has
 * chosen "Accept all". This module is the single source of truth for that
 * choice: it persists it, exposes it synchronously (so the banner and any
 * gated script agree on state without a flash on return visits), and
 * broadcasts changes so already-mounted components can react live.
 *
 * "Essential only" and "Accept all" are both terminal, explicit choices —
 * dismissing the banner without picking one does NOT count as consent, and
 * nothing gated reads a choice until one of these two functions is called.
 */

export type ConsentChoice = 'accepted' | 'essential' | null;

const STORAGE_KEY = 'thornless-cookie-consent';
const CONSENT_EVENT = 'thornless-consent-change';

export function getConsent(): ConsentChoice {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'accepted' || stored === 'essential' ? stored : null;
}

export function setConsent(choice: Exclude<ConsentChoice, null>): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, choice);
  window.dispatchEvent(new CustomEvent<ConsentChoice>(CONSENT_EVENT, { detail: choice }));
}

/** Subscribe to consent changes. Returns an unsubscribe function. */
export function onConsentChange(handler: (choice: ConsentChoice) => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<ConsentChoice>).detail);
  window.addEventListener(CONSENT_EVENT, listener);
  return () => window.removeEventListener(CONSENT_EVENT, listener);
}
